import appModule from '../../app.module';

export default appModule.factory('accountService', [
    '$http',
    'serviceRoot',
    'authenticationService',
    '__env',
    '$q',
    '$sessionStorage',
    'CacheFactory',
    AccountService
]);

function AccountService(
    $http,
    serviceRoot,
    authenticationService,
    __env,
    $q,
    $sessionStorage,
    CacheFactory
) {
    return {
        getBillingContactInformation: getBillingContactInformation,
        getAccountGuid: getAccountGuid,
        getTotalUsage: getTotalUsage,
        getInstances: getInstances,
        getSubscriptionEntitlement: getSubscriptionEntitlement,
        getPlanInformation: getPlanInformation,
        getBillingInformation: getBillingInformation,
        applyReceipt: applyReceipt,
        createInstance: createInstance,
        getInstanceActivation: getInstanceActivation,
        get64CharPayload: get64CharPayload,
        manageInstanceAllocation: manageInstanceAllocation,
        getInstanceDetail: getInstanceDetail,
        updateInstance: updateInstance,
        deleteInstanceActivation: deleteInstanceActivation,
        resendActivationEmail: resendActivationEmail,
        getLicenseTypes: getLicenseTypes
    };

    var accountCache;

    function setupCache() {
        if (!CacheFactory.get('accountCache')) {
            accountCache = CacheFactory('accountCache', {
                maxAge: 2000,
                deleteOnExpire: 'passive'
            });
        }
    }

    function getBillingContactInformation() {
        $http.get(serviceRoot.getBaseUrl() + 'api/v1/appspace/accounts');
    }

    function getAccountGuid() {
        if ($sessionStorage.accountGuid) {
            return $q.resolve($sessionStorage.accountGuid);
        }

        return $q((resolve, reject) => {
            authenticationService.getToken().then(
                cloudToken => {
                    $http
                        .get(
                            `${
                                __env.cloudApiUrl
                            }/api/v1/appspace/token/${cloudToken}`
                        )
                        .then(
                            function(response) {
                                $sessionStorage.accountGuid =
                                    response.data.Account.Guid;
                                resolve($sessionStorage.accountGuid);
                            },
                            response => {
                                serviceRoot.errorStatusFilter(response);
                                reject();
                            }
                        );
                },
                () => reject()
            );
        });
    }

    function getTotalUsage() {
        setupCache();
        var defer = $q.defer();
        getAccountGuid().then(function(accountGuid) {
            $http
                .get(
                    __env.cloudApiUrl +
                        '/api/v2/accounts/' +
                        accountGuid +
                        '/usage',
                    {
                        cache: accountCache
                    }
                )
                .then(function(response) {
                    return defer.resolve(response.data);
                }, serviceRoot.errorStatusFilter);
        });
        return defer.promise;
    }

    function getSubscriptionEntitlement() {
        setupCache();
        var defer = $q.defer();
        getAccountGuid().then(function(accountGuid) {
            $http
                .get(
                    __env.cloudApiUrl +
                        '/api/v2/accounts/' +
                        accountGuid +
                        '/entitlement',
                    {
                        cache: accountCache
                    }
                )
                .then(function(response) {
                    return defer.resolve(response.data);
                }, serviceRoot.errorStatusFilter);
        });
        return defer.promise;
    }

    function getInstances() {
        var defer = $q.defer();
        getAccountGuid().then(function(accountGuid) {
            $http
                .get(
                    __env.cloudApiUrl +
                        '/api/v2/accounts/' +
                        accountGuid +
                        '/instances'
                )
                .then(function(response) {
                    return defer.resolve(response.data.items);
                }, serviceRoot.errorStatusFilter);
        });
        return defer.promise;
    }

    function getPlanInformation() {
        setupCache();
        var defer = $q.defer();
        getAccountGuid().then(function(accountGuid) {
            $http
                .get(
                    __env.cloudApiUrl +
                        '/api/v2/accounts/' +
                        accountGuid +
                        '/plan',
                    {
                        cache: accountCache
                    }
                )
                .then(function(response) {
                    return defer.resolve(response.data);
                }, serviceRoot.errorStatusFilter);
        });
        return defer.promise;
    }

    function getBillingInformation() {
        var deferred = $q.defer();
        getAccountGuid().then(function(accountGuid) {
            $http
                .get(
                    __env.cloudApiUrl +
                        '/api/v2/accounts/' +
                        accountGuid +
                        '/billing'
                )
                .then(function(response) {
                    deferred.resolve(response.data);
                }, serviceRoot.errorStatusFilter);
        });
        return deferred.promise;
    }

    function applyReceipt(instanceGuid, receipt) {
        var defer = $q.defer();
        getAccountGuid().then(function(accountGuid) {
            $http
                .post(
                    __env.cloudApiUrl +
                        '/api/v2/accounts/' +
                        accountGuid +
                        '/instances/' +
                        instanceGuid +
                        '/activation/receipt',
                    receipt
                )
                .then(
                    function(response) {
                        return defer.resolve(response.data);
                    },
                    function(response) {
                        serviceRoot.errorStatusFilter(response);
                        return defer.reject(response.data);
                    }
                );
        });
        return defer.promise;
    }

    function createInstance(instance) {
        var defer = $q.defer();
        getAccountGuid().then(function(accountGuid) {
            $http
                .post(
                    __env.cloudApiUrl +
                        '/api/v2/accounts/' +
                        accountGuid +
                        '/instances',
                    instance
                )
                .then(
                    function(response) {
                        return defer.resolve(response.data);
                    },
                    function(response) {
                        serviceRoot.errorStatusFilter(response);
                        return defer.reject(response.data);
                    }
                );
        });
        return defer.promise;
    }

    function getInstanceActivation(instanceGuid) {
        var defer = $q.defer();

        getAccountGuid().then(function(accountGuid) {
            $http
                .get(
                    __env.cloudApiUrl +
                        '/api/v2/accounts/' +
                        accountGuid +
                        '/instances/' +
                        instanceGuid +
                        '/activation'
                )
                .then(
                    function(response) {
                        return defer.resolve(response.data);
                    },
                    function(response) {
                        serviceRoot.errorStatusFilter(response);
                        return defer.reject(response.data);
                    }
                );
        });
        return defer.promise;
    }

    function get64CharPayload(payload) {
        var defer = $q.defer();
        try {
            payload = payload.trim();
            var newLineChar = /\n/g;
            var regexMarker = /([-]*[\s\w]+[-]+\r?\n?)([\w]+=*\r?\n?)([-]*[\s\w]+[-]+\r?\n?)/g;
            payload = payload.replace(newLineChar, '');
            var arrayRegex = regexMarker.exec(payload);
            var regexChop = new RegExp('(.{' + 64 + '})', 'g');
            var jwtToken = arrayRegex[2].replace(regexChop, '$1\n');
            var choppedPayload =
                arrayRegex[1].trim() +
                '\n' +
                jwtToken.trim() +
                '\n' +
                arrayRegex[3].trim();
            defer.resolve(choppedPayload);
        } catch (err) {
            defer.reject();
        }
        return defer.promise;
    }

    function manageInstanceAllocation(instanceGuid, allocation) {
        var defer = $q.defer();
        getAccountGuid().then(function(accountGuid) {
            $http
                .put(
                    __env.cloudApiUrl +
                        '/api/v2/accounts/' +
                        accountGuid +
                        '/instances/' +
                        instanceGuid +
                        '/allocation',
                    allocation
                )
                .then(
                    function(response) {
                        return defer.resolve(response.data);
                    },
                    function(response) {
                        serviceRoot.errorStatusFilter(response);
                        return defer.reject(response.data);
                    }
                );
        });
        return defer.promise;
    }

    function getInstanceDetail(instanceGuid) {
        var defer = $q.defer();
        getAccountGuid().then(function(accountGuid) {
            $http
                .get(
                    __env.cloudApiUrl +
                        '/api/v2/accounts/' +
                        accountGuid +
                        '/instances/' +
                        instanceGuid +
                        '/serverprofile'
                )
                .then(
                    function(response) {
                        return defer.resolve(response.data);
                    },
                    function(response) {
                        serviceRoot.errorStatusFilter(response);
                        return defer.reject(response.data);
                    }
                );
        });
        return defer.promise;
    }

    function updateInstance(instanceGuid, instance) {
        var defer = $q.defer();
        getAccountGuid().then(function(accountGuid) {
            $http
                .put(
                    __env.cloudApiUrl +
                        '/api/v2/accounts/' +
                        accountGuid +
                        '/instances/' +
                        instanceGuid,
                    instance
                )
                .then(
                    function(response) {
                        return defer.resolve(response.data);
                    },
                    function(response) {
                        serviceRoot.errorStatusFilter(response);
                        return defer.reject(response.data);
                    }
                );
        });
        return defer.promise;
    }

    function deleteInstanceActivation(instanceGuid) {
        var defer = $q.defer();
        getAccountGuid().then(function(accountGuid) {
            $http
                .delete(
                    __env.cloudApiUrl +
                        '/api/v2/accounts/' +
                        accountGuid +
                        '/instances/' +
                        instanceGuid +
                        '/activation'
                )
                .then(
                    function(response) {
                        return defer.resolve(response.data);
                    },
                    function(response) {
                        serviceRoot.errorStatusFilter(response);
                        return defer.reject(response.data);
                    }
                );
        });
        return defer.promise;
    }

    function resendActivationEmail(recaptchaHeader) {
        const defer = $q.defer();

        getAccountGuid().then(function(accountGuid) {
            $http
                .post(
                    `${
                        __env.cloudApiUrl
                    }/api/v2/accounts/${accountGuid}/activation/sendemail`,
                    null,
                    { headers: { 'Recaptcha-Token': recaptchaHeader } }
                )
                .then(
                    function(response) {
                        return defer.resolve(response.data);
                    },
                    function(response) {
                        serviceRoot.errorStatusFilter(response);
                        return defer.reject(response.data);
                    }
                );
        });

        return defer.promise;
    }

    function getLicenseTypes() {
        const defer = $q.defer();

        authenticationService.getToken().then(cloudToken => {
            $http
                .get(`${__env.cloudApiUrl}/api/v2/licenses/licensetypes`, {
                    headers: { token: cloudToken }
                })
                .then(
                    function(response) {
                        defer.resolve(response.data);
                    },
                    function(response) {
                        serviceRoot.errorStatusFilter(response);
                        return defer.reject(response.data);
                    }
                );
        });

        return defer.promise;
    }
}



// WEBPACK FOOTER //
// ./shared/services/accountService.service.js